/**
 * Context-aware routing utility for SO (Scheduled Offering) links
 * 
 * This utility helps components generate the correct URL for viewing SO details
 * based on the current context (main app, sales dashboard, or admin dashboard).
 * 
 * @param {string} soNumber - The SO number to link to
 * @param {object} options - Options for context detection
 * @param {Location} options.location - React Router location object (from useLocation hook)
 * @param {boolean} options.isInSalesContext - Override to force sales context
 * @param {boolean} options.isInAdminContext - Override to force admin context
 * @returns {string} The appropriate URL for the SO
 */
export function getViewSOLink(soNumber, options = {}) {
  const { location, isInSalesContext, isInAdminContext } = options;
  
  // Validate SO number
  if (!soNumber || typeof soNumber !== 'string') {
    if (process.env.NODE_ENV === 'development') {
      console.warn('getViewSOLink: Invalid SO number provided', soNumber);
    }
    return '/';
  }
  
  // Use explicit context overrides first
  if (isInSalesContext === true) {
    return `/sales/scheduled-offerings/search?so=${soNumber}`;
  }
  
  if (isInAdminContext === true) {
    return `/admin/scheduled-offerings/search?so=${soNumber}`;
  }
  
  // Auto-detect context from current location
  if (location && location.pathname) {
    const currentPath = location.pathname;
    
    // Check if we're in sales context
    if (currentPath === '/sales' || currentPath.startsWith('/sales/')) {
      return `/sales/scheduled-offerings/search?so=${soNumber}`;
    }
    
    // Check if we're in admin context
    if (currentPath === '/admin' || currentPath.startsWith('/admin/')) {
      return `/admin/scheduled-offerings/search?so=${soNumber}`;
    }
  }
  
  // Default to main app routing (backward compatibility)
  return `/${soNumber}`;
}

/**
 * Determines the current dashboard context based on the pathname
 * 
 * @param {string} pathname - Current pathname from location
 * @returns {string} 'sales' | 'admin' | 'main'
 */
export function getCurrentContext(pathname) {
  if (!pathname) return 'main';
  
  if (pathname === '/sales' || pathname.startsWith('/sales/')) return 'sales';
  if (pathname === '/admin' || pathname.startsWith('/admin/')) return 'admin';
  return 'main';
}